import { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { Modal, ModalBody, ModalFooter, Button, Input, Form, Select, ToggleSwitch } from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';

//Helpers
import { getUnitTypeDatabases } from 'Core/data/Helpers';

//Actions 
import {
    clearRedux,
    unitTypeCreateRequest,
    unitTypeUpdateRequest,
    unitTypeGetRequest,
    searchEntityRequest,
} from 'Redux/actions';

const CreateEditUnitType = (props) => {
    const { manufacturer, tableRef } = props;
    const { messages } = useIntl();
    const formRef = useRef();
    const dispatch = useDispatch();

    //Local States
    const [unitType, setUnitType] = useState("");
    const [unitTypeTitle, setUnitTypeTitle] = useState("");
    const [deviceModel, setDeviceModel] = useState(null);
    const [newwork, setNewwork] = useState(null);

    const [name, setName] = useState("");
    const [database, setDatabase] = useState(null);
    const [usesSim, setUsesSim] = useState(true);
    const [useImeiAsSerial, setUseImeiAsSerial] = useState(false);
    const [active, setActive] = useState(true);


    //Redux States
    const { openModal, loading, create, update, current, unittype } = useSelector(state => state.createEditUnitTypeRedux);
    const user = useSelector(state => state.securityRedux.user);

    const closeModal = () => {
        dispatch(clearRedux("CREATE_EDIT_UNITTYPES"));
        setName("");
        setDatabase(null);
        setUsesSim(true);
        setUseImeiAsSerial(false);
        setActive(true);
    }

    const applyChanges = () => {
        let payload = {}
        if (!current) {
            payload = {
                name: name,
                manufacturerId: manufacturer.id,
                database: database.id,
                typeSim: usesSim,
                typeUseImei: useImeiAsSerial,
                active: active
            }
            dispatch(unitTypeCreateRequest(payload));
        } else {
            payload = {
                id: current.id,
                name: name,
                typeSim: usesSim,
                typeUseImei: useImeiAsSerial,
                active: active
            }
            dispatch(unitTypeUpdateRequest(payload));
        }
    }

    useEffect(() => {
        if (openModal && current) {
            dispatch(unitTypeGetRequest(current?.id))
        }
    }, [openModal])

    useEffect(() => {

        if (create?.id) {
            closeModal();
            tableRef && tableRef?.current?.getData();
            dispatch(clearRedux("CREATE_EDIT_UNITTYPES", { create: null }));
            dispatch(searchEntityRequest({ key: "unitTypes" }))
            NotificationManager.success(
                messages['create_unittype_create_success_message'],
                messages['create_unittype_create_title'],
                3000
            );
        } else if (create?.error) {
            let message = messages['create_unittype_error_create_general']
            if (create?.error.includes("exists already")) {
                message = messages['create_unittype_error_same_name']
            }
            NotificationManager.error(
                message,
                messages['create_unittype_create_title'],
                3000
            );
            dispatch(clearRedux("CREATE_EDIT_UNITTYPES", { create: null }));
        }
        if (update?.id) {
            closeModal();
            tableRef && tableRef?.current?.getData();
            dispatch(clearRedux("CREATE_EDIT_UNITTYPES", { update: null }));
            dispatch(searchEntityRequest({ key: "unitTypes" }))
            NotificationManager.success(
                messages['create_unittype_update_success_message'],
                messages['create_unittype_edit_title'],
                3000
            );
        }else if(update?.error){
            let message = messages['create_unittype_error_update_general']
            if (update?.error.includes("exists already")) {
                message = messages['create_unittype_error_same_name']
            }
            NotificationManager.error(
                message,
                messages['create_unittype_create_title'],
                3000
            );
            dispatch(clearRedux("CREATE_EDIT_UNITTYPES", { update: null }));
        }
        if (unittype) {
            unittype?.name && setName(unittype?.name);
            setUsesSim(unittype?.typeSim);
            setUseImeiAsSerial(unittype?.typeUseImei);
            setActive(unittype?.active);
            setUnitTypeTitle(messages['create_unittype_edit_title']);


            dispatch(clearRedux("CREATE_EDIT_UNITTYPES", { unittype: null }))
        }
    }, [create, update, unittype])

    return (
        <Modal
            open={openModal}
            title={current ? `${messages['create_unittype_edit_title']} ${unitTypeTitle ? ` (${unitTypeTitle})` : ''}` : messages['create_unittype_create_title']}
            handleClose={() => closeModal()}
            position="right"
            loading={loading}
            size="small"
        >
            <ModalBody>
                <div style={{ padding: '0 10px' }}>
                    <Form
                        ref={formRef}
                        onValidSubmit={() => applyChanges()}
                    >
                        <div className="manufacturer-input">
                            <Input
                                label={messages['create_unittype_manufacturer']}
                                placeholder={messages['create_unittype_manufacturer']}
                                value={manufacturer?.name || ""}
                                onChange={() => { }}
                                disabled
                            />
                        </div>
                        <div style={{ marginTop: 5 }}>
                            <Input
                                label={messages['create_unittype_name']}
                                placeholder={messages['create_unittype_name']}
                                value={name}
                                onChange={(value) => setName(value)}
                                required
                                requiredMessage={messages["common_field_required"]}
                                maxLength={25}
                            />
                        </div>
                        {
                            !current &&
                            <div style={{ marginTop: 5 }}>
                                <Select
                                    id="databases-list"
                                    label={messages['create_unittype_database']}
                                    placeholder={messages['create_unittype_database']}
                                    options={getUnitTypeDatabases() || []}
                                    onChange={(value) => setDatabase(value)}
                                    value={database}
                                    textField="value"
                                    valueField="id"
                                    isClearable
                                    required
                                    requiredMessage={messages["common_field_required"]}
                                />
                            </div>
                        }
                        <div style={{ textAlign: 'left', display: 'grid', padding: '0 10px', marginTop: 5 }}>
                            <label style={{ marginBottom: 5 }} className="input-normal-label">{messages['create_unittype_uses_sim']}</label>
                            <ToggleSwitch
                                id="unit-type-uses-sim"
                                medium
                                blueToggle
                                optionLabels={[messages['common_yes'], messages['common_no']]}
                                checked={usesSim}
                                onChange={(checked) => setUsesSim(checked)}
                            />
                        </div>
                        <div style={{ textAlign: 'left', display: 'grid', padding: '0 10px', marginTop: 5 }}>
                            <label style={{ marginBottom: 5 }} className="input-normal-label">{messages['create_unittype_use_imei_as_serial']}</label>
                            <ToggleSwitch
                                id="unit-type-use-as-serial-number"
                                medium
                                blueToggle
                                optionLabels={[messages['common_yes'], messages['common_no']]}
                                checked={useImeiAsSerial}
                                onChange={(checked) => setUseImeiAsSerial(checked)}
                            />
                        </div>
                        <div style={{ textAlign: 'left', display: 'grid', padding: '0 10px', marginTop: 5 }}>
                            <label style={{ marginBottom: 5 }} className="input-normal-label">{messages['create_unittype_active']}</label>
                            <ToggleSwitch
                                id="unit-type-active"
                                medium
                                blueToggle
                                optionLabels={[messages['common_yes'], messages['common_no']]}
                                checked={active}
                                onChange={(checked) => setActive(checked)}
                            />
                        </div>
                    </Form>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    className="btn-cancel"
                    onClick={() => closeModal()}
                >
                    {messages['cancel']}
                </Button>
                <Button
                    className="btn-dark-blue"
                    onClick={() => formRef?.current?.submit()}
                >
                    {messages['save']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default CreateEditUnitType;